import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Lock,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Calendar,
  Server,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { ScoreBar } from "../ModuleScore";
import { IssuesList, type Issue } from "../IssuesList";
import { Badge } from "../../badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";

// TLS Module Data Types
interface CertificateIssuer {
  organization?: string;
  common_name?: string;
  country?: string;
}

interface Certificate {
  days_until_expiry?: number;
  not_before?: string;
  not_after?: string;
  issuer?: CertificateIssuer;
  serial_number?: string;
  key_bits?: number;
  key_algorithm?: string;
  signature_algorithm?: string;
  is_self_signed?: boolean;
  san?: string[];
  subject?: string;
}

interface ChainQuality {
  is_complete?: boolean;
  is_ordered?: boolean;
  has_trusted_root?: boolean;
}

interface TlsHostResult {
  host: string;
  port?: number;
  protocol_version?: string;
  cipher_suite?: string;
  certificate?: Certificate;
  chain_quality?: ChainQuality;
  issues?: Array<{ code?: string; message?: string } | string>;
}

interface CtCertificate {
  serial_number?: string;
  issuer?: string;
  not_before?: string;
  not_after?: string;
  is_expired?: boolean;
}

interface InventorySummary {
  total_certificates?: number;
  expired_certificates?: number;
  expiring_soon?: number;
  unusual_issuer_count?: number;
  wildcard_certificates?: number;
}

export interface TlsModuleData {
  overall_score?: number;
  hosts_scanned?: number;
  hosts_with_tls?: number;
  results?: TlsHostResult[];
  ct_certificates?: CtCertificate[];
  inventory_summary?: InventorySummary;
  issues?: Array<string | Issue>;
}

export interface TlsModuleViewProps {
  data: TlsModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Days until expiry badge
function ExpiryBadge({ days }: { days?: number }) {
  if (days === undefined) {
    return (
      <Badge variant="outline" className="text-xs text-muted-foreground">
        Unknown
      </Badge>
    );
  }

  if (days <= 0) {
    return (
      <Badge
        variant="outline"
        className="text-xs bg-red-500/15 text-red-400 border-red-500/40"
      >
        Expired
      </Badge>
    );
  }

  if (days <= 30) {
    return (
      <Badge
        variant="outline"
        className="text-xs bg-red-500/15 text-red-400 border-red-500/40"
      >
        {days} days left
      </Badge>
    );
  }

  if (days <= 90) {
    return (
      <Badge
        variant="outline"
        className="text-xs bg-amber-500/15 text-amber-400 border-amber-500/40"
      >
        {days} days left
      </Badge>
    );
  }

  return (
    <Badge
      variant="outline"
      className="text-xs bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
    >
      {days} days left
    </Badge>
  );
}

// Protocol version badge
function ProtocolBadge({ version }: { version?: string }) {
  if (!version) return null;

  const isSecure = version.includes("1.3") || version.includes("1.2");
  const isModern = version.includes("1.3");

  return (
    <Badge
      variant="outline"
      className={cn(
        "text-xs",
        isModern
          ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40"
          : isSecure
          ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/40"
          : "bg-red-500/15 text-red-400 border-red-500/40"
      )}
    >
      {version}
    </Badge>
  );
}

// Chain quality indicators
function ChainQualityDisplay({ chain }: { chain?: ChainQuality }) {
  if (!chain) return null;

  const checks = [
    {
      label: "Complete Chain",
      value: chain.is_complete,
      description: "All intermediate certificates present",
    },
    {
      label: "Proper Order",
      value: chain.is_ordered,
      description: "Certificates in correct order",
    },
    {
      label: "Trusted Root",
      value: chain.has_trusted_root,
      description: "Root CA is trusted",
    },
  ];

  return (
    <div className="space-y-2">
      {checks.map((check, i) => (
        <div key={i} className="flex items-center gap-2">
          {check.value ? (
            <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          ) : check.value === false ? (
            <XCircle className="h-4 w-4 text-red-400" />
          ) : (
            <div className="h-4 w-4 rounded-full bg-muted" />
          )}
          <span className="text-sm">{check.label}</span>
        </div>
      ))}
    </div>
  );
}

// Certificate card
function CertificateCard({ cert }: { cert: Certificate }) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center gap-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <Shield className="h-5 w-5 text-cyan-400" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">
            {cert.issuer?.organization || cert.issuer?.common_name || "Unknown Issuer"}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            {cert.subject || "Unknown Subject"}
          </p>
        </div>
        <ExpiryBadge days={cert.days_until_expiry} />
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          {/* Validity Period */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              Validity Period
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Valid From</p>
                <p className="font-mono text-sm">
                  {cert.not_before
                    ? new Date(cert.not_before).toLocaleDateString()
                    : "Unknown"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Valid To</p>
                <p className="font-mono text-sm">
                  {cert.not_after
                    ? new Date(cert.not_after).toLocaleDateString()
                    : "Unknown"}
                </p>
              </div>
            </div>
          </div>

          {/* Key Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Key Size</p>
              <p className="font-mono text-sm">
                {cert.key_bits ? `${cert.key_bits} bits` : "Unknown"}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Algorithm</p>
              <p className="font-mono text-sm">
                {cert.signature_algorithm || cert.key_algorithm || "Unknown"}
              </p>
            </div>
          </div>

          {/* Self-signed warning */}
          {cert.is_self_signed && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              <span className="text-sm text-amber-400">Self-signed certificate</span>
            </div>
          )}

          {/* SANs */}
          {cert.san && cert.san.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Subject Alternative Names ({cert.san.length})
              </p>
              <div className="flex flex-wrap gap-1">
                {cert.san.slice(0, 10).map((name, i) => (
                  <Badge key={i} variant="outline" className="text-xs font-mono">
                    {name}
                  </Badge>
                ))}
                {cert.san.length > 10 && (
                  <Badge variant="outline" className="text-xs">
                    +{cert.san.length - 10} more
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Serial Number */}
          {cert.serial_number && (
            <div>
              <p className="text-xs text-muted-foreground">Serial Number</p>
              <p className="font-mono text-xs truncate">{cert.serial_number}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Host result accordion
function HostResultCard({ result }: { result: TlsHostResult }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hostIssues = result.issues?.length || 0;

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center gap-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        <Server className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-sm">
            {result.host}
            {result.port && result.port !== 443 && (
              <span className="text-muted-foreground">:{result.port}</span>
            )}
          </p>
        </div>
        <ProtocolBadge version={result.protocol_version} />
        {result.certificate && (
          <ExpiryBadge days={result.certificate.days_until_expiry} />
        )}
        {hostIssues > 0 && (
          <Badge
            variant="outline"
            className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
          >
            {hostIssues} issue{hostIssues > 1 ? "s" : ""}
          </Badge>
        )}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {isExpanded && (
        <div className="p-4 border-t border-border space-y-4">
          {/* TLS Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Protocol</p>
              <p className="font-mono text-sm">{result.protocol_version || "Unknown"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Cipher Suite</p>
              <p className="font-mono text-xs truncate" title={result.cipher_suite}>
                {result.cipher_suite || "Unknown"}
              </p>
            </div>
          </div>

          {/* Certificate */}
          {result.certificate && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Certificate
              </p>
              <CertificateCard cert={result.certificate} />
            </div>
          )}

          {/* Chain Quality */}
          {result.chain_quality && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Chain Quality
              </p>
              <ChainQualityDisplay chain={result.chain_quality} />
            </div>
          )}

          {/* Host Issues */}
          {result.issues && result.issues.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
                Issues
              </p>
              <div className="space-y-2">
                {result.issues.map((issue, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 p-2 rounded bg-amber-500/5 border border-amber-500/20"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                    <span className="text-sm text-amber-400">
                      {typeof issue === "string" ? issue : issue.message || issue.code}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Inventory summary cards
function InventorySummaryCards({ summary }: { summary: InventorySummary }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal">
            Total Certs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold font-mono">
            {summary.total_certificates ?? 0}
          </p>
        </CardContent>
      </Card>

      <Card
        className={cn(
          "bg-card/50",
          (summary.expired_certificates ?? 0) > 0 && "border-red-500/30"
        )}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal">
            Expired
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p
            className={cn(
              "text-2xl font-bold font-mono",
              (summary.expired_certificates ?? 0) > 0
                ? "text-red-400"
                : "text-emerald-400"
            )}
          >
            {summary.expired_certificates ?? 0}
          </p>
        </CardContent>
      </Card>

      <Card
        className={cn(
          "bg-card/50",
          (summary.expiring_soon ?? 0) > 0 && "border-amber-500/30"
        )}
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal">
            Expiring Soon
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p
            className={cn(
              "text-2xl font-bold font-mono",
              (summary.expiring_soon ?? 0) > 0 ? "text-amber-400" : "text-foreground"
            )}
          >
            {summary.expiring_soon ?? 0}
          </p>
        </CardContent>
      </Card>

      <Card className="bg-card/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs text-muted-foreground font-normal">
            Wildcards
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold font-mono">
            {summary.wildcard_certificates ?? 0}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// Convert string issues to Issue objects
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];

  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "medium";
      let message = issue;

      // Parse severity prefix from backend (e.g., "[INFO] host: message")
      const prefixMatch = message.match(/^\[(INFO|WARNING|MEDIUM|HIGH|CRITICAL|LOW)\]\s*/i);
      if (prefixMatch) {
        const level = prefixMatch[1].toUpperCase();
        message = message.slice(prefixMatch[0].length);
        if (level === "INFO") severity = "info";
        else if (level === "LOW") severity = "low";
        else if (level === "MEDIUM" || level === "WARNING") severity = "medium";
        else if (level === "HIGH") severity = "high";
        else if (level === "CRITICAL") severity = "critical";
      } else if (
        message.toLowerCase().includes("expired") ||
        message.toLowerCase().includes("revoked")
      ) {
        severity = "critical";
      } else if (
        message.toLowerCase().includes("weak") ||
        message.toLowerCase().includes("self-signed") ||
        message.toLowerCase().includes("untrusted")
      ) {
        severity = "high";
      } else if (message.toLowerCase().includes("expiring")) {
        severity = "medium";
      }

      return {
        id: `tls-issue-${index}`,
        severity,
        message,
        module: "tls",
      };
    }
    return issue;
  });
}

export function TlsModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: TlsModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const hostsScanned = data.hosts_scanned ?? 0;
  const hostsWithTls = data.hosts_with_tls ?? 0;
  const tlsPercentage = hostsScanned > 0 ? Math.round((hostsWithTls / hostsScanned) * 100) : 0;

  return (
    <ModuleContainer
      moduleName="tls"
      icon={Lock}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.overall_score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              Hosts Scanned
            </p>
            <p className="text-2xl font-bold font-mono">{hostsScanned}</p>
          </div>
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
              With TLS
            </p>
            <p className="text-2xl font-bold font-mono text-emerald-400">
              {hostsWithTls}
            </p>
          </div>
          <div className="p-4 rounded-lg bg-muted/30 border border-border col-span-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">
              TLS Coverage
            </p>
            <ScoreBar score={tlsPercentage} showValue size="md" />
          </div>
        </div>

        {/* Inventory Summary */}
        {data.inventory_summary && (
          <ModuleSection title="Certificate Inventory" icon={Shield}>
            <InventorySummaryCards summary={data.inventory_summary} />
          </ModuleSection>
        )}

        {/* Host Results */}
        {data.results && data.results.length > 0 && (
          <ModuleSection
            title="Host Results"
            icon={Server}
            badge={
              <Badge variant="outline" className="text-xs">
                {data.results.length} hosts
              </Badge>
            }
          >
            <div className="space-y-3">
              {data.results.map((result, index) => (
                <HostResultCard key={index} result={result} />
              ))}
            </div>
          </ModuleSection>
        )}

        {/* CT Certificates */}
        {data.ct_certificates && data.ct_certificates.length > 0 && (
          <ModuleSection
            title="Certificate Transparency Log"
            icon={Calendar}
            badge={
              <Badge variant="outline" className="text-xs">
                {data.ct_certificates.length} certs
              </Badge>
            }
            defaultExpanded={false}
          >
            <div className="space-y-2">
              {data.ct_certificates.map((cert, index) => (
                <div
                  key={index}
                  className={cn(
                    "flex items-center gap-4 p-3 rounded-lg border",
                    cert.is_expired
                      ? "border-muted bg-muted/20"
                      : "border-border bg-muted/30"
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs truncate">
                      {cert.serial_number || "Unknown Serial"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {cert.issuer || "Unknown Issuer"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Expires</p>
                    <p className="font-mono text-xs">
                      {cert.not_after
                        ? new Date(cert.not_after).toLocaleDateString()
                        : "Unknown"}
                    </p>
                  </div>
                  {cert.is_expired ? (
                    <Badge
                      variant="outline"
                      className="text-xs text-muted-foreground"
                    >
                      Expired
                    </Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                    >
                      Active
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={
              <Badge
                variant="outline"
                className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400"
              >
                {issues.length}
              </Badge>
            }
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

TlsModuleView.displayName = "TlsModuleView";
